// KAN-163: TOTP enrolment through Cognito's user-facing API, authorised by the
// user's own access token (scope aws.cognito.signin.user.admin). The Hosted UI
// only shows MFA setup when the pool has MFA=ON, and under ON an admin cannot
// reset a lost authenticator, so the pool runs OPTIONAL and the portal enforces
// enrolment itself (see RequireMfa).
import { ApiClient } from './apiClient';

const REGION = process.env.REACT_APP_COGNITO_REGION || 'eu-west-2';

async function cognito<T>(target: string, body: Record<string, unknown>): Promise<T> {
  const accessToken = ApiClient.getAccessToken();
  if (!accessToken) throw new Error('Not signed in');
  const res = await fetch(`https://cognito-idp.${REGION}.amazonaws.com/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': `AWSCognitoIdentityProviderService.${target}`,
    },
    body: JSON.stringify({ AccessToken: accessToken, ...body }),
    signal: AbortSignal.timeout(10000),
  });
  const data = await res.json();
  if (!res.ok) {
    const err = new Error(data.message || data.__type || 'Cognito request failed');
    err.name = data.__type || 'CognitoError';
    throw err;
  }
  return data as T;
}

/** True when the signed-in user has an authenticator app enabled. */
export async function hasTotpMfa(): Promise<boolean> {
  const data = await cognito<{ UserMFASettingList?: string[] }>('GetUser', {});
  return (data.UserMFASettingList || []).includes('SOFTWARE_TOKEN_MFA');
}

/** Issues a new TOTP secret. Replaces any existing authenticator once confirmed. */
export async function startTotpEnrolment(): Promise<string> {
  const data = await cognito<{ SecretCode: string }>('AssociateSoftwareToken', {});
  return data.SecretCode;
}

/** Confirms the first code from the app and switches TOTP on for the user. */
export async function confirmTotpEnrolment(code: string): Promise<void> {
  const data = await cognito<{ Status: string }>('VerifySoftwareToken', { UserCode: code });
  if (data.Status !== 'SUCCESS') throw new Error('Code not accepted');
  await cognito('SetUserMFAPreference', {
    SoftwareTokenMfaSettings: { Enabled: true, PreferredMfa: true },
  });
}

/** otpauth:// URI for the QR code. Issuer is the portal hostname so dev and prod differ. */
export function totpUri(secret: string, email: string): string {
  const issuer = typeof window !== 'undefined' ? window.location.hostname : 'SimScope';
  return `otpauth://totp/${encodeURIComponent(issuer)}:${encodeURIComponent(email)}?secret=${secret}&issuer=${encodeURIComponent(issuer)}`;
}
